import { env } from "./env"
import type { AuthUser } from "@/features/auth/types"
import type { UserSubscription } from "@/features/subscriptions/types"

/**
 * DEV-ONLY auth + subscription bypass — opens the webapp with no Firebase login
 * and no real subscription check, for local development/testing.
 *
 * Safety: gated on BOTH conditions, so it can NEVER activate in production:
 *  1. `import.meta.env.DEV` — true only under `vite dev`; a production `vite build`
 *     inlines it as `false`, so the whole branch is dead-code-eliminated.
 *  2. an explicit env flag — stays off unless you opt in locally via `.env.local`
 *     (`VITE_DEV_BYPASS_AUTH=true`). `.env.local` is gitignored, so it never ships.
 *
 * Note: live chat/socket features still need a real Firebase session and won't
 * work under the bypass — this is only meant to reach and test the UI.
 */
export const DEV_BYPASS_AUTH = import.meta.env.DEV && env.VITE_DEV_BYPASS_AUTH === "true"

/**
 * DEV-ONLY report-purchase mock. When on, a "Get" tap simulates a successful
 * charge — skipping the real subscription/email checks and the payment provider —
 * so the full post-purchase UX (processing spinner → unlock timer → "Report in
 * Progress" sheet → reorder to end of list) can be exercised locally without a
 * real account or a real payment.
 *
 * Toggle via `.env.local` (`VITE_DEV_MOCK_PURCHASE=true`); OFF by default, so a
 * normal checkout runs the real path. Independent of DEV_BYPASS_AUTH — enable
 * either or both. Gated on `import.meta.env.DEV`, so production `vite build`
 * dead-code-eliminates it and it can never ship. See the "Reports purchase — dev
 * mock" card in Documentation for the full rationale.
 */
export const DEV_MOCK_PURCHASE = import.meta.env.DEV && env.VITE_DEV_MOCK_PURCHASE === "true"

/**
 * Fake signed-in user. The `uid: "dev-user"` sentinel is intentionally preserved
 * by the auth listener (see `use-auth.ts`) so a stray Firebase event can't clear it.
 */
export const DEV_BYPASS_USER: AuthUser = {
  uid: "dev-user",
  email: "dev@local.test",
  displayName: "Dev User",
  appleToken: null,
}

/** Fake active subscription so gated screens (My Plan, etc.) render with data. */
export const DEV_BYPASS_SUBSCRIPTION: UserSubscription = {
  id: "dev-subscription",
  bundle: env.VITE_SUBSCRIPTION_BUNDLE || "lpr_web",
  planCode: "lpr_web_weekly_dev",
  status: "active",
  isMainPlan: true,
  startedAt: "2025-01-01",
}
